import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth as dateFnsIsSameMonth,
  isSameDay as dateFnsIsSameDay,
  addMonths,
  subMonths,
  isValid,
  parseISO,
  startOfWeek,
  endOfWeek,
  addDays,
  subDays,
  isToday,
  isBefore,
  isAfter,
  differenceInDays,
} from 'date-fns';

/**
 * Parse date string in various formats used by BigTime
 */
export const parseDate = (dateStr: string): Date | null => {
  if (!dateStr || typeof dateStr !== 'string') return null;

  const trimmed = dateStr.trim();
  if (!trimmed) return null;

  try {
    // Try ISO format first
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      const parsed = parseISO(trimmed);
      return isValid(parsed) ? parsed : null;
    }

    // Handle MM/DD/YY format
    if (/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(trimmed)) {
      const [month, day, year] = trimmed.split('/');
      const fullYear = parseInt(year) < 50 ? `20${year}` : `19${year}`;
      const parsed = new Date(`${month}/${day}/${fullYear}`);
      return isValid(parsed) ? parsed : null;
    }

    // Handle MM/DD/YYYY format
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
      const parsed = new Date(trimmed);
      return isValid(parsed) ? parsed : null;
    }

    // Handle M/D/YY format
    if (/^\d{1}\/\d{1}\/\d{2}$/.test(trimmed)) {
      const [month, day, year] = trimmed.split('/');
      const fullYear = parseInt(year) < 50 ? `20${year}` : `19${year}`;
      const parsed = new Date(`${month}/${day}/${fullYear}`);
      return isValid(parsed) ? parsed : null;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Format date for display
 */
export const formatDate = (date: Date, formatStr: string): string => {
  if (!isValid(date)) return '';
  return format(date, formatStr);
};

/**
 * Get start of month
 */
export const getStartOfMonth = (date: Date): Date => {
  return startOfMonth(date);
};

/**
 * Get end of month
 */
export const getEndOfMonth = (date: Date): Date => {
  return endOfMonth(date);
};

/**
 * Get all days in a month
 */
export const getDaysInMonth = (date: Date): Date[] => {
  const start = startOfMonth(date);
  const end = endOfMonth(date);
  return eachDayOfInterval({ start, end });
};

/**
 * Get calendar grid days (including prev/next month days for full weeks)
 */
export const getCalendarDays = (date: Date): Date[] => {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
};

/**
 * Check if two dates are in the same month
 */
export const isSameMonth = dateFnsIsSameMonth;

/**
 * Check if two dates are the same day
 */
export const isSameDay = dateFnsIsSameDay;

/**
 * Add months to a date (using date-fns)
 */
export { addMonths, subMonths };

/**
 * Check if date is today (using date-fns)
 */
export { isToday };

/**
 * Check if date is in the past
 */
export const isPast = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return isBefore(compareDate, today);
};

/**
 * Check if date is in the future
 */
export const isFuture = (date: Date): boolean => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return isAfter(date, today);
};

/**
 * Get days until a date
 */
export const getDaysUntil = (date: Date): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return differenceInDays(compareDate, today);
};

/**
 * Get relative time description
 */
export const getRelativeTime = (date: Date): string => {
  const days = getDaysUntil(date);

  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 1 && days <= 7) return `In ${days} days`;
  if (days < -1 && days >= -7) return `${Math.abs(days)} days ago`;
  if (days > 7) return `In ${Math.ceil(days / 7)} weeks`;
  if (days < -7) return `${Math.ceil(Math.abs(days) / 7)} weeks ago`;

  return formatDate(date, 'MMM d, yyyy');
};

/**
 * Get date type color based on the type of calendar event
 */
export const getDateTypeColor = (dateType: 'due' | 'span' | 'additional' | 'trial'): string => {
  const colors = {
    due: '#E9594E',      // Vermilion
    span: '#193CBA',     // Persian Blue
    additional: '#EDE65A', // Maize
    trial: '#193CBA',    // Persian Blue (same as span)
  };
  return colors[dateType] || colors.additional;
};

/**
 * Get business days between two dates (excluding weekends)
 */
export const getBusinessDays = (startDate: Date, endDate: Date): number => {
  let count = 0;
  const current = new Date(startDate);

  while (current <= endDate) {
    const dayOfWeek = current.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      // Not Sunday (0) or Saturday (6)
      count++;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
};

/**
 * Get week number of the year
 */
export const getWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

/**
 * Create date range
 */
export const createDateRange = (start: Date, end: Date): Date[] => {
  return eachDayOfInterval({ start, end });
};

/**
 * Get current quarter
 */
export const getCurrentQuarter = (date: Date = new Date()): { quarter: number; year: number } => {
  const month = date.getMonth();
  const quarter = Math.floor(month / 3) + 1;
  return {
    quarter,
    year: date.getFullYear(),
  };
};

/**
 * Format date for BigTime API
 */
export const formatForApi = (date: Date): string => {
  return formatDate(date, 'yyyy-MM-dd');
};

/**
 * Parse and validate date range
 */
export const validateDateRange = (
  start: string | Date,
  end: string | Date
): {
  start: Date | null;
  end: Date | null;
  isValid: boolean;
} => {
  const startDate = typeof start === 'string' ? parseDate(start) : start;
  const endDate = typeof end === 'string' ? parseDate(end) : end;

  const isValid = !!(startDate && endDate && isBefore(startDate, endDate));

  return {
    start: startDate,
    end: endDate,
    isValid,
  };
};

// Export common date-fns functions for convenience
export {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addDays,
  subDays,
  isValid,
  isBefore,
  isAfter,
  differenceInDays,
};
